'use client';
import React, { useState } from 'react';
import ScrollReveal from '@/components/ScrollReveal';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { siteConfig } from '../../../config/site';

const faqs = [
  {
    q: 'What devices do you repair?',
    a: 'We repair a wide range of devices including iPhones, Samsung phones, iPads, laptops, MacBooks, desktop computers, PS5, PS4, Xbox, Nintendo Switch, and general electronics. Call us to ask about your specific device.',
  },
  {
    q: 'Do you repair PS5, PS4, Xbox and Nintendo Switch?',
    a: 'Yes. Gaming console repair and cleaning is one of our specialties. We handle PS5 HDMI port repair, overheating issues, deep cleaning, PS4 repair, Xbox repair and cleaning, and Nintendo Switch repair. Not every issue can be diagnosed without inspecting the console — bring it in or call us for details.',
  },
  {
    q: 'Do you offer same-day repair?',
    a: 'Same-day repair is available on many repairs. The timeline depends on the device, the issue, and parts availability. Call for details on your specific situation.',
  },
  {
    q: 'How much does a repair cost?',
    a: 'Repair pricing depends on the device, model, and the issue. We don\'t publish fixed prices because every repair is different. Call for details and we\'ll give you an honest assessment.',
  },
  {
    q: 'Can I request service at my home or office?',
    a: 'Yes. We offer on-site service at homes, offices, and company locations anywhere in New York State. Submit a request through our form and we\'ll call you to confirm the details.',
  },
  {
    q: 'Do you serve areas outside New York City?',
    a: 'Yes. Our shop is in Long Island City, Queens. We also offer on-site service across New York State — including Long Island, upstate New York, and all five boroughs.',
  },
  {
    q: 'Do I need an appointment?',
    a: 'No appointment is required for shop visits. Just walk in during our business hours: Monday–Saturday 11:30 AM–7:30 PM, Sunday 1:00 PM–8:00 PM.',
  },
  {
    q: 'How does the AI Repair Assistant work?',
    a: 'The AI Repair Assistant gives you general first-step guidance based on your symptoms. It\'s not a final diagnosis — a team member always confirms the details. It\'s a helpful starting point if you\'re not sure what\'s wrong with your device.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="section-padding" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>
              FAQ
            </p>
            <h2 className="text-display font-bold" style={{ color: 'var(--text)' }}>
              Common Questions
            </h2>
          </div>
        </ScrollReveal>

        <div className="space-y-2">
          {faqs?.map((faq, i) => (
            <ScrollReveal key={i} delay={i * 40}>
              <div
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-color)' }}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:opacity-80"
                  aria-expanded={openIndex === i}
                >
                  <span className="text-sm font-semibold pr-4" style={{ color: 'var(--text)' }}>{faq?.q}</span>
                  <ChevronDownIcon
                    className="w-5 h-5 flex-shrink-0 transition-transform duration-200"
                    style={{
                      color: 'var(--text-muted)',
                      transform: openIndex === i ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </button>
                {openIndex === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{faq?.a}</p>
                  </div>
                )}
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={200}>
          <div className="mt-8 text-center">
            <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
              Still have questions?
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href={siteConfig?.phone?.tel}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                style={{ backgroundColor: 'var(--brand)', color: 'var(--bg)' }}
              >
                Call Us
              </a>
              <a
                href={siteConfig?.phone?.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold border transition-all hover:opacity-80"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text)' }}
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}